import { Box, Chip, Paper, Stack } from '@mui/material'
import React from 'react'

import GlyphIcon from 'components/common/GlyphIcon'
import GlyphSubIcon from 'components/common/GlyphSubIcon'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import {
  getLogo,
  getPresetKeyFromURL
} from 'components/scenarios/create/status-checks/third-party-presets'
import { getStatusCheckDetails } from 'components/scenarios/utils'
import { getStateOfNodeByType } from 'components/scenarios/utils/graph-node-lifecycle'
import {
  isCustomStatusCheck,
  StatusCheckCategoryLabel
} from 'components/status-checks/status-checks-form-utils'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { trans } from 'i18n/translate'
import { muiTheme } from 'mui-theme'
import theme from 'theme'
import {
  ContinuousStatusCheckNode,
  SynchronousStatusCheckNode
} from 'types/api/scenarios/scenario'
import {
  type StatusCheck,
  StatusCheckCategory
} from 'types/api/status-checks/status-check'

/* Paper props wrapper values */
const NODE_MIN_WIDTH = 500
const NODE_MAX_WIDTH = 800

/* Derived from the width set in the component Paper wrapper */
const NODE_TEXT_MAX_WIDTH = NODE_MAX_WIDTH - 100

const CATEGORY_FALLBACK_NAME = 'Unknown category'

/* ((Number of chars) * (estimated width of char)) + (gap size) */
const CATEGORY_CHIP_MAX_WIDTH = CATEGORY_FALLBACK_NAME.length * 8.25 + 28

const ICON_STYLES = {
  height: muiTheme.spacing(4),
  width: muiTheme.spacing(4)
}

/**
 * Gets a status check category display name for display in the chip.
 * @param statusCheck - the type is overloaded to include 2 possibilities:
 * - string: name of Intelligent Health Check (which is predictable and unchanging)
 * - StatusCheck - should have the `category` property, which is an enum
 */
function getCategoryDisplayName(statusCheck: StatusCheck | string | undefined) {
  if (typeof statusCheck === 'string') {
    const name = statusCheck.toLowerCase()

    if (name.includes('error')) {
      return StatusCheckCategoryLabel.ERRORS
    }
    if (name.includes('latency')) {
      return StatusCheckCategoryLabel.LATENCY
    }
    if (name.includes('request')) {
      return StatusCheckCategoryLabel.REQUESTS
    }

    return CATEGORY_FALLBACK_NAME // it shouldn't reach this point, provided just in case
  }

  const category = statusCheck?.category

  if (!category || category === StatusCheckCategory.UNKNOWN) {
    return CATEGORY_FALLBACK_NAME
  }

  return StatusCheckCategoryLabel[category]
}

/**
 * If the endpoint url is one of the known services, then replace the hostname with the service name
 * Otherwise return the hostname portion of the url
 */
function parseDisplayUrl(url?: string) {
  return url && url.replace('https://', '')
}

export const StatusCheckGraphNode: React.FC<{
  node: SynchronousStatusCheckNode | ContinuousStatusCheckNode
  index?: number
  overview?: boolean
  rowActions?: React.ReactNode
  isClickable?: boolean
  onClick?: (
    node: SynchronousStatusCheckNode | ContinuousStatusCheckNode
  ) => void
}> = ({
  node,
  index = 1,
  rowActions = null,
  isClickable = false,
  onClick = () => null
}) => {
  const {
    reliabilityIntelligenceRunSuggestions:
      hasEnabledReliabilityIntelligenceRunSuggestions
  } = useFeatureFlags()
  const { statusChecksRaw } = useStatusChecksContext()

  const { isNotStarted, isActive } = getStateOfNodeByType(node)

  const endpointUrl = node.endpointConfiguration?.url
  const displayUrl = parseDisplayUrl(endpointUrl)

  const nodeStatusCheck = statusChecksRaw.find(
    (statusCheck) => statusCheck.identifier === node?.statusCheckId
  )

  const isCustom = node.isAutoGenerated
    ? false
    : isCustomStatusCheck(nodeStatusCheck)

  const { color, icon } = getStatusCheckDetails(node)

  const category =
    hasEnabledReliabilityIntelligenceRunSuggestions &&
    getCategoryDisplayName(node.isAutoGenerated ? node.name : nodeStatusCheck)

  return (
    <Paper
      sx={(muiTheme) => ({
        width: 'fit-content',
        minWidth: NODE_MIN_WIDTH,
        maxWidth: NODE_MAX_WIDTH,
        padding: 1,
        marginY: muiTheme.spacing(2)
      })}
      {...(isClickable && { onClick: () => onClick(node) })}
    >
      <Stack
        aria-label={`${trans('status_checks.label.singular')} ${index + 1}`}
        direction="row"
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', columnGap: 1.5 }}>
          <GlyphIcon
            backgroundColor={isCustom ? theme.colors.steel[2] : 'none'}
            css={ICON_STYLES}
          >
            {node.isAutoGenerated ? (
              <Icon css={ICON_STYLES} icon="statusCheckGremlinIcon" />
            ) : isCustom ? (
              <Icon
                css={{
                  ...ICON_STYLES,
                  color: theme.colors.gray[1]
                }}
                icon="wavePulse"
              />
            ) : (
              <img
                css={ICON_STYLES}
                src={getLogo(
                  getPresetKeyFromURL(node?.endpointConfiguration?.url)
                )}
              />
            )}
            {!isNotStarted && !isActive && (
              <GlyphSubIcon
                css={{
                  background: color,
                  height: muiTheme.spacing(2),
                  width: muiTheme.spacing(2),
                  bottom: 0,
                  right: `-${muiTheme.spacing(1)}`,
                  border: `2px solid ${muiTheme.palette.common.white}`,
                  padding: 0
                }}
              >
                <Icon
                  css={{
                    margin: 'auto'
                  }}
                  icon={icon}
                />
              </GlyphSubIcon>
            )}
          </GlyphIcon>
          <Stack>
            <Stack
              direction="row"
              sx={{ alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}
            >
              <TruncatedTextWithTooltip
                data-test-status-check-name
                aria-label={`${trans('status_checks.label.singular')} ${
                  node.name
                }`}
                sx={{
                  fontSize: muiTheme.typography.body2.fontSize,
                  fontWeight: muiTheme.typography.fontWeightBold,
                  maxWidth: `calc(${NODE_TEXT_MAX_WIDTH}px - ${
                    category ? CATEGORY_CHIP_MAX_WIDTH : 0
                  }px)`
                }}
                tooltipProps={{ title: node.name }}
                variant="h4"
              >
                {node.name}
              </TruncatedTextWithTooltip>
              {category && <Chip label={category} />}
            </Stack>
            {!isClickable && (
              <TruncatedTextWithTooltip
                data-test-status-check-url
                aria-label={`URL: ${endpointUrl}`}
                sx={{ maxWidth: NODE_TEXT_MAX_WIDTH }}
                tooltipProps={{ title: endpointUrl }}
                variant="caption"
              >
                {displayUrl}
              </TruncatedTextWithTooltip>
            )}
          </Stack>
        </Stack>

        {rowActions && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignContent: 'center'
            }}
          >
            {rowActions}
          </Box>
        )}
      </Stack>
    </Paper>
  )
}
